import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faAngleLeft,
  faAnglesLeft,
  faAngleRight,
  faAnglesRight,
  faAngleDown,
  faAngleUp,
  faImages,
  faUsers,
  faFlag,
  faRobot,
  faCogs,
  faCog,
  faSearch,
  faTrash,
  faExclamationTriangle,
  faCheck,
  faCheckCircle,
  faTimes,
  faNotesMedical,
  faCalendarMinus,
  faThumbsDown,
  faExclamation,
  faQrcode,
  faPencilAlt,
  faStickyNote,
  faFileDownload,
  faExclamationCircle,
  faFileExport,
  faUser,
  faPhone,
  faEnvelope,
  faArrowUpRightFromSquare,
  faBook,
  faFileExcel,
  faXmarkCircle,
  faFileCircleCheck,
  faCircleQuestion,
  faFilePdf,
  faPlayCircle,
  faPrint,
} from "@fortawesome/free-solid-svg-icons";
import { HotkeysProvider } from "react-hotkeys-hook";
import { useSelector } from "react-redux";

import AuthGate from "shared/components/AuthGate";
import AuthUserGate from "shared/components/AuthUserGate";
import { GateProps } from "shared/components/GateProps";
import StainGate from "shared/components/StainGate";
import useInteractionListener from "shared/hooks/useInteractionListener";
import {
  selectMfaSetupRequired,
  selectIsAccessTokenExpired,
  selectIsRefreshTokenExpired,
  selectLongLivedToken,
} from "shared/reducers/authReducer";

import AppError from "AppError";
import AppRoutes, { LongLivedTokenRoutes } from "AppRoutes";
import AppSpinner from "AppSpinner";
import MacroCodeGate from "components/gates/MacroCodeGate";
import MfaMethodSelectionPage from "components/pages/MfaMethodSelectionPage";
import LoginRoutes from "LoginRoutes";

import DictationOverlay from "./components/dictation/DictationOverlay";
import ImpersonationBanner from "./components/ImpersonationBanner";

library.add(
  faAngleLeft,
  faAnglesLeft,
  faAngleRight,
  faAnglesRight,
  faAngleDown,
  faAngleUp,
  faImages,
  faUsers,
  faFlag,
  faRobot,
  faCogs,
  faCog,
  faSearch,
  faTrash,
  faExclamationTriangle,
  faExclamationCircle,
  faCheck,
  faCheckCircle,
  faXmarkCircle,
  faTimes,
  faNotesMedical,
  faCalendarMinus,
  faThumbsDown,
  faExclamation,
  faQrcode,
  faPencilAlt,
  faStickyNote,
  faFileCircleCheck,
  faFileDownload,
  faFileExport,
  faFilePdf,
  faUser,
  faPhone,
  faEnvelope,
  faArrowUpRightFromSquare,
  faBook,
  faFileExcel,
  faCircleQuestion,
  faPlayCircle,
  faPrint
);

const App = () => {
  const isAccessTokenExpired = useSelector(selectIsAccessTokenExpired);
  const isRefreshTokenExpired = useSelector(selectIsRefreshTokenExpired);
  const mfaSetupRequired = useSelector(selectMfaSetupRequired);
  const longLivedToken = useSelector(selectLongLivedToken);

  useInteractionListener();

  if (isAccessTokenExpired && isRefreshTokenExpired) return <LoginRoutes />;

  if (mfaSetupRequired) return <MfaMethodSelectionPage />;

  const gateProps: Omit<GateProps, "children"> = {
    LoadingEl: AppSpinner,
    ErrorEl: AppError,
  };

  if (longLivedToken) {
    // This is exclusively used by the Report Generator
    return (
      <AuthGate {...gateProps}>
        <StainGate LoadingEl={() => <></>} ErrorEl={AppError} justStains>
          <LongLivedTokenRoutes />
        </StainGate>
      </AuthGate>
    );
  }

  return (
    <AuthGate {...gateProps}>
      <AuthUserGate {...gateProps}>
        <MacroCodeGate {...gateProps}>
          <StainGate {...gateProps}>
            <HotkeysProvider initiallyActiveScopes={["viewerHotkeys"]}>
              <ImpersonationBanner />
              <DictationOverlay />
              <AppRoutes />
            </HotkeysProvider>
          </StainGate>
        </MacroCodeGate>
      </AuthUserGate>
    </AuthGate>
  );
};

export default App;
